(function (globalScope) {
  function toNonNegativeInteger(value) {
    const parsedValue = parseInt(value, 10);
    return Number.isNaN(parsedValue) ? 0 : Math.max(0, parsedValue);
  }

  function normalizeTier({ minimumQuantityText, discountType, discountValue }) {
    const minimumQuantityMatch = String(minimumQuantityText || '').match(/\d+/);
    const minimumQuantity = minimumQuantityMatch ? parseInt(minimumQuantityMatch[0], 10) : 0;
    const normalizedType = String(discountType || '').trim().toLowerCase();
    const normalizedValue = Number(discountValue);

    if (!minimumQuantity || !Number.isFinite(normalizedValue) || normalizedValue < 0) return null;

    if (['percentage_off_each', 'percent_off_each'].includes(normalizedType)) {
      return {
        minimumQuantity,
        type: 'percentage',
        percent: Math.abs(normalizedValue),
      };
    }

    if (['dollar_off_each', 'amount_off_each', 'fixed_amount_each'].includes(normalizedType)) {
      return {
        minimumQuantity,
        type: 'fixed_amount',
        amountCents: Math.abs(Math.round(normalizedValue)),
      };
    }

    return null;
  }

  function getActiveTier(tiers, totalQuantity) {
    if (!Array.isArray(tiers) || totalQuantity <= 0) return null;

    return tiers.reduce((activeTier, tier) => {
      if (tier && totalQuantity >= tier.minimumQuantity) return tier;
      return activeTier;
    }, null);
  }

  function applyTier(tier, sellingTotalCents, totalQuantity) {
    const safeSellingTotal = toNonNegativeInteger(sellingTotalCents);
    const safeQuantity = toNonNegativeInteger(totalQuantity);
    if (!tier || safeQuantity <= 0) return safeSellingTotal;

    if (tier.type === 'fixed_amount') {
      return Math.max(0, safeSellingTotal - toNonNegativeInteger(tier.amountCents) * safeQuantity);
    }

    if (tier.type === 'percentage') {
      const percent = Math.min(100, Math.max(0, Number(tier.percent) || 0));
      return Math.max(0, Math.round((safeSellingTotal * (100 - percent)) / 100));
    }

    return safeSellingTotal;
  }

  function aggregateSelections(selections) {
    return (Array.isArray(selections) ? selections : []).reduce(
      (totals, selection) => {
        const quantity = toNonNegativeInteger(selection?.quantity);
        const priceCents = toNonNegativeInteger(selection?.priceCents);
        const compareAtPriceCents = toNonNegativeInteger(selection?.compareAtPriceCents);
        const referencePriceCents = compareAtPriceCents > priceCents ? compareAtPriceCents : priceCents;

        totals.totalQuantity += quantity;
        totals.sellingTotalCents += quantity * priceCents;
        totals.referenceTotalCents += quantity * referencePriceCents;
        return totals;
      },
      {
        totalQuantity: 0,
        sellingTotalCents: 0,
        referenceTotalCents: 0,
      }
    );
  }

  function calculateDisplayTotals({ totalQuantity, sellingTotalCents, referenceTotalCents, tiers }) {
    const safeQuantity = toNonNegativeInteger(totalQuantity);
    const safeSellingTotal = toNonNegativeInteger(sellingTotalCents);
    const safeReferenceTotal = Math.max(safeSellingTotal, toNonNegativeInteger(referenceTotalCents));
    const activeTier = getActiveTier(tiers, safeQuantity);
    const payableTotalCents = applyTier(activeTier, safeSellingTotal, safeQuantity);

    return {
      totalQuantity: safeQuantity,
      payableTotalCents,
      referenceTotalCents: safeReferenceTotal,
      hasSavings: payableTotalCents < safeReferenceTotal,
    };
  }

  function parseDiscountTable(table) {
    if (!table?.querySelectorAll) return [];

    return Array.from(table.querySelectorAll('tbody tr'))
      .map((row) => {
        const cells = Array.from(row.querySelectorAll('td'));
        if (!cells.length) return null;

        const metadataCell = cells.find(
          (cell) => cell.dataset?.shopacadoDiscountType && cell.dataset?.shopacadoDiscountVal !== undefined
        );
        if (!metadataCell) return null;

        const minimumQuantityText = cells[0].dataset?.shopacadoTemplate || cells[0].textContent;
        return normalizeTier({
          minimumQuantityText,
          discountType: metadataCell.dataset.shopacadoDiscountType,
          discountValue: metadataCell.dataset.shopacadoDiscountVal,
        });
      })
      .filter(Boolean)
      .sort((tierA, tierB) => tierA.minimumQuantity - tierB.minimumQuantity);
  }

  function formatMoney(cents) {
    if (globalScope?.FoxTheme?.Currency?.formatMoney) {
      return globalScope.FoxTheme.Currency.formatMoney(cents, globalScope.FoxTheme.settings?.moneyFormat);
    }

    return `$${(toNonNegativeInteger(cents) / 100).toFixed(2)}`;
  }

  function renderMainPrice(priceElement, totals, totalLabel) {
    const price = priceElement?.querySelector('.f-price');
    const regularPrice = price?.querySelector('.f-price__regular .f-price-item--regular');
    const salePrice = price?.querySelector('.f-price__sale .f-price-item--sale');
    const comparePrice = price?.querySelector('.f-price__sale .f-price-item--regular');
    const unitPrice = price?.querySelector('.f-price__unit-wrapper');

    if (!price || !regularPrice || !salePrice || !comparePrice) return false;

    price.classList.remove('f-price--sold-out');
    unitPrice?.classList.add('hidden');

    if (totals.hasSavings) {
      price.classList.add('f-price--on-sale');
      salePrice.innerHTML = formatMoney(totals.payableTotalCents);
      salePrice.insertAdjacentText('afterbegin', `${totalLabel}: `);
      comparePrice.innerHTML = `<s>${formatMoney(totals.referenceTotalCents)}</s>`;
    } else {
      price.classList.remove('f-price--on-sale');
      regularPrice.innerHTML = formatMoney(totals.payableTotalCents);
      regularPrice.insertAdjacentText('afterbegin', `${totalLabel}: `);
      salePrice.innerHTML = '';
      comparePrice.innerHTML = '';
    }

    return true;
  }

  const pricing = {
    aggregateSelections,
    applyTier,
    calculateDisplayTotals,
    getActiveTier,
    normalizeTier,
    parseDiscountTable,
    renderMainPrice,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = pricing;
  }

  if (globalScope) {
    globalScope.FoxTheme = globalScope.FoxTheme || {};
    globalScope.FoxTheme.ShopacadoPricing = pricing;

    if (!globalScope.customElements.get('shopacado-product-pricing')) {
      globalScope.customElements.define(
        'shopacado-product-pricing',
        class ShopacadoProductPricing extends globalScope.HTMLElement {
          constructor() {
            super();
            this.handleQuantityChange = this.handleQuantityChange.bind(this);
            this.handleTableMutations = this.handleTableMutations.bind(this);
          }

          connectedCallback() {
            this.productInfo = this.closest('product-info');
            if (!this.productInfo || this.productInfo.querySelector('variant-table')) return;

            this.sectionId = this.dataset.sectionId;
            this.totalLabel = this.dataset.totalLabel || 'Total';
            this.priceCents = toNonNegativeInteger(this.dataset.price);
            this.compareAtPriceCents = toNonNegativeInteger(this.dataset.compareAtPrice);
            this.quantityInput = this.productInfo.querySelector(
              `#QuantityForm-${this.sectionId} .quantity__input[name="quantity"]`
            );
            this.mainPriceElement = this.productInfo.querySelector(`[id="price-${this.sectionId}"]`);
            this.originalMainPriceMarkup = this.mainPriceElement?.innerHTML || '';
            this.discountTiers = [];
            this.isShopacadoActive = false;
            this.isMainPriceSynced = false;
            this.refreshTimeout = null;
            this.retryTimeouts = [];

            if (!this.quantityInput || !this.mainPriceElement) return;

            this.quantityInput.addEventListener('input', this.handleQuantityChange);
            this.quantityInput.addEventListener('change', this.handleQuantityChange);

            if (globalScope.FoxTheme?.pubsub?.subscribe && globalScope.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
              this.unsubscribeVariantChange = globalScope.FoxTheme.pubsub.subscribe(
                globalScope.FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
                (event) => this.handleVariantChange(event)
              );
            }

            if (globalScope.MutationObserver) {
              this.tableObserver = new globalScope.MutationObserver(this.handleTableMutations);
              this.tableObserver.observe(this.productInfo, {
                attributes: true,
                childList: true,
                characterData: true,
                subtree: true,
              });
            }

            this.refreshDiscountTiers();
            [250, 1000].forEach((delay) => {
              this.retryTimeouts.push(globalScope.setTimeout(() => this.refreshDiscountTiers(), delay));
            });
          }

          disconnectedCallback() {
            this.quantityInput?.removeEventListener('input', this.handleQuantityChange);
            this.quantityInput?.removeEventListener('change', this.handleQuantityChange);
            this.tableObserver?.disconnect();
            if (typeof this.unsubscribeVariantChange === 'function') this.unsubscribeVariantChange();
            if (this.refreshTimeout) globalScope.clearTimeout(this.refreshTimeout);
            this.retryTimeouts?.forEach((timeoutId) => globalScope.clearTimeout(timeoutId));
          }

          findDiscountTable() {
            return this.productInfo?.querySelector('.shopacado-discount-table') || null;
          }

          handleQuantityChange() {
            this.updatePrice();
          }

          handleVariantChange(event) {
            const eventData = event?.data;
            if (eventData?.sectionId && eventData.sectionId !== this.sectionId) return;

            const variant = eventData?.variant;
            if (!variant) return;

            this.priceCents = toNonNegativeInteger(variant.price);
            this.compareAtPriceCents = toNonNegativeInteger(variant.compare_at_price);
            this.originalMainPriceMarkup = this.mainPriceElement?.innerHTML || this.originalMainPriceMarkup;
            this.isMainPriceSynced = false;

            try {
              globalScope.shopacado?.productUpdateWithNewPrice?.(variant.price);
            } catch (error) {
              if (globalScope.shopacado?.debug) console.warn('Unable to update the Shopacado table price.', error);
            }

            this.updatePrice();
          }

          handleTableMutations(mutations) {
            const nodeType = globalScope.Node?.ELEMENT_NODE || 1;
            const touchesDiscountTable = mutations.some((mutation) => {
              const element = mutation.target?.nodeType === nodeType ? mutation.target : mutation.target?.parentElement;
              if (element?.closest?.('.shopacado-discount-table, .shopacado-discount-table-block')) return true;

              return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
                if (node?.nodeType !== nodeType) return false;
                return Boolean(
                  node.matches?.('.shopacado-discount-table, .shopacado-discount-table-block') ||
                    node.querySelector?.('.shopacado-discount-table')
                );
              });
            });

            if (!touchesDiscountTable) return;
            if (this.refreshTimeout) globalScope.clearTimeout(this.refreshTimeout);
            this.refreshTimeout = globalScope.setTimeout(() => {
              this.refreshDiscountTiers();
              this.refreshTimeout = null;
            }, 120);
          }

          restoreMainPrice() {
            if (!this.mainPriceElement || !this.isMainPriceSynced) return;
            this.mainPriceElement.innerHTML = this.originalMainPriceMarkup;
            this.isMainPriceSynced = false;
          }

          refreshDiscountTiers() {
            const table = this.findDiscountTable();
            this.isShopacadoActive = Boolean(table);
            this.discountTiers = table ? parseDiscountTable(table) : [];

            if (!this.isShopacadoActive) {
              this.restoreMainPrice();
              return;
            }

            this.updatePrice();
          }

          updatePrice() {
            if (!this.isShopacadoActive || !this.quantityInput || !this.mainPriceElement) return;

            const quantity = toNonNegativeInteger(this.quantityInput.value);
            if (quantity <= 0) {
              this.restoreMainPrice();
              return;
            }

            const baseTotals = aggregateSelections([
              {
                quantity,
                priceCents: this.priceCents,
                compareAtPriceCents: this.compareAtPriceCents,
              },
            ]);
            const totals = calculateDisplayTotals({
              ...baseTotals,
              tiers: this.discountTiers,
            });

            this.isMainPriceSynced = renderMainPrice(this.mainPriceElement, totals, this.totalLabel);
          }
        }
      );
    }
  }
})(typeof window !== 'undefined' ? window : null);
